import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Leaf, Award, TrendingUp, Calendar, ArrowLeft, Scale } from 'lucide-react';
import CheckinModal from '@/components/CheckinModal';
import { useToast } from '@/components/ui/use-toast';

function MKDietal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [streak, setStreak] = useState({ streak: 0, mensaje: '', proximo_galon: null });
  const [stats, setStats] = useState({ verde: 0, amarillo: 0, rojo: 0, total: 0, porcentaje_verde: 0 });
  const [badges, setBadges] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem('token');

  const [isCheckinOpen, setIsCheckinOpen] = useState(false);
  const [checkinStatus, setCheckinStatus] = useState(null);
  const [monthlyCheckins, setMonthlyCheckins] = useState([]);
  const { toast } = useToast();

  const isPro = user?.es_pro || user?.role === 'entrenador';

  useEffect(() => {
    fetchData();
    if (isPro) {
      fetchCheckinStatus();
      fetchMonthlyCheckins();
    }
  }, [currentMonth]);

  const fetchCheckinStatus = async () => {
    try {
      const res = await axios.get(`${API}/checkin/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCheckinStatus(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchMonthlyCheckins = async () => {
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      const res = await axios.get(`${API}/checkin/monthly/${year}/${month}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonthlyCheckins(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCheckinComplete = () => {
    fetchCheckinStatus();
    fetchMonthlyCheckins();
    toast({
      title: "Check-in completado!",
      description: "Tus datos han sido registrados. Sigue asi!",
      className: "bg-green-600 border-green-700 text-white"
    });
  };

  const fetchData = async () => {
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      
      const [entriesRes, streakRes, statsRes, badgesRes] = await Promise.all([
        axios.get(`${API}/mkdietal/entries?year=${year}&month=${month}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/mkdietal/streak`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/mkdietal/stats?year=${year}&month=${month}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/mkdietal/badges`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setEntries(entriesRes.data);
      setStreak(streakRes.data);
      setStats(statsRes.data);
      setBadges(badgesRes.data);
    } catch (error) {
      console.error('Error fetching MKdietal data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleColorSelect = async (color) => {
    if (!selectedDate || submitting) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/mkdietal/entry`, {
        fecha: selectedDate,
        color: color
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await fetchData();
      setSelectedDate(null);
    } catch (error) {
      console.error('Error saving entry:', error);
      alert(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  const getColorForDate = (dateStr) => {
    const entry = entries.find(e => e.fecha === dateStr);
    return entry?.color || null;
  };

  const getColorClass = (color) => {
    switch (color) {
      case 'verde': return 'bg-green-500';
      case 'amarillo': return 'bg-yellow-500';
      case 'rojo': return 'bg-red-500';
      default: return 'bg-gray-700';
    }
  };

  const isCheckinDoneForDate = (dateStr) => {
    return monthlyCheckins.some(c => c.date && c.date.startsWith(dateStr));
  };

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const days = [];
    const dayNames = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];
    
    dayNames.forEach(day => {
      days.push(
        <div key={`header-${day}`} className={`text-center text-xs font-medium py-2 ${day === 'Vie' && isPro ? 'text-purple-400' : 'text-gray-400'}`}>
          {day}
        </div>
      );
    });
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="p-1"></div>);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dateObj = new Date(year, month, day);
      const dayOfWeek = dateObj.getDay();
      const color = getColorForDate(dateStr);
      const isToday = dateObj.getTime() === today.getTime();
      const isPast = dateObj < today;
      const isSelected = selectedDate === dateStr;
      const canSelect = isPast || isToday;
      
      const isFriday = dayOfWeek === 5;
      const showCheckinIndicator = isPro && isFriday;
      const checkinDone = showCheckinIndicator && isCheckinDoneForDate(dateStr);
      const isTodayFriday = isToday && isFriday;
      const canOpenCheckin = isPro && isTodayFriday && !checkinStatus?.completed;

      days.push(
        <div 
          key={dateStr}
          data-testid={`day-${dateStr}`}
          onClick={() => {
            if (canOpenCheckin) {
              setIsCheckinOpen(true);
            } else if (canSelect) {
              setSelectedDate(isSelected ? null : dateStr);
            }
          }}
          className={`
            relative p-1 aspect-square flex items-center justify-center rounded-lg
            transition-all duration-200 cursor-pointer overflow-hidden
            ${color ? getColorClass(color) : 'bg-gray-800 hover:bg-gray-700'}
            ${isToday ? 'ring-2 ring-gold' : ''}
            ${isSelected ? 'ring-2 ring-white scale-110 z-10' : ''}
            ${showCheckinIndicator && !isPast && !isToday ? 'opacity-40 cursor-not-allowed' : ''}
            ${!canSelect && !showCheckinIndicator ? 'opacity-40 cursor-not-allowed' : ''}
            ${canOpenCheckin ? 'ring-2 ring-purple-500 animate-pulse' : ''}
          `}
        >
          {/* Franja purpura para viernes PRO */}
          {showCheckinIndicator && (isPast || isToday) && (
            <div className={`absolute inset-x-0 bottom-0 h-2 flex items-center justify-center
              ${checkinDone ? 'bg-purple-600' : isTodayFriday ? 'bg-purple-500 animate-pulse' : 'bg-purple-900/50'}
            `}>
              {checkinDone && (
                <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </div>
          )}
          
          {/* Icono de bascula para viernes abierto */}
          {canOpenCheckin && (
            <Scale className="absolute top-0.5 right-0.5 w-2.5 h-2.5 text-purple-400" />
          )}
          
          <span className={`text-sm font-medium ${color ? 'text-white' : 'text-gray-300'}`}>
            {day}
          </span>
        </div>
      );
    }
    
    return days;
  };

  const changeMonth = (delta) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + delta);
      return newDate;
    });
    setSelectedDate(null);
  };

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-mk flex items-center justify-center">
        <div className="text-gold text-xl">Cargando MKdietal...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-mk p-4 pb-20" data-testid="mkdietal-page">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <Button variant="ghost" onClick={() => navigate('/')} className="text-gray-400 hover:text-white">
            <ArrowLeft className="h-5 w-5 mr-1" />
            Volver
          </Button>
        </div>

        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gold flex items-center justify-center gap-2">
            <Leaf className="h-8 w-8" />
            MKdietal
          </h1>
          <p className="text-gray-400 mt-1">Sistema de autoevaluacion alimentaria</p>
        </div>

        {/* Banner PRO Check-in - Siempre visible si no ha hecho checkin esta semana */}
        {isPro && checkinStatus && !checkinStatus.completed && (
          <Card className="border-purple-500 bg-purple-900/20 cursor-pointer hover:bg-purple-900/30 transition-colors"
                onClick={() => setIsCheckinOpen(true)}
                data-testid="checkin-banner">
            <CardContent className="p-4 flex items-center gap-4">
              <Scale className="h-8 w-8 text-purple-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-purple-300 font-bold">{new Date().getDay() === 5 ? 'Viernes MK Control!' : 'Check-in semanal pendiente'}</p>
                <p className="text-purple-400 text-sm">Registra tu medicion semanal para tu programa de transformacion.</p>
              </div>
              <Button className="bg-purple-600 hover:bg-purple-500 text-white" data-testid="checkin-banner-btn">
                Registrar
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Check-in completado esta semana */}
        {isPro && checkinStatus && checkinStatus.completed && (
          <Card className="border-green-700 bg-green-900/20" data-testid="checkin-done-banner">
            <CardContent className="p-4 flex items-center gap-3">
              <Scale className="h-6 w-6 text-green-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-green-300 font-medium">Check-in semanal completado</p>
                <p className="text-green-400/70 text-sm">Peso: {checkinStatus.data?.weight} kg {checkinStatus.data?.body_fat ? `| Grasa: ${checkinStatus.data.body_fat}%` : ''}</p>
              </div>
              <svg className="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </CardContent>
          </Card>
        )}

        {/* Racha actual */}
        <Card className="card-mk border-gray-800 bg-gradient-to-br from-gray-900 to-gray-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Tu racha actual</p>
                <p className="text-4xl font-bold text-green-500">{streak.streak} dias</p>
                <p className="text-gold mt-2">{streak.mensaje}</p>
              </div>
              {streak.proximo_galon && (
                <div className="text-right">
                  <p className="text-gray-400 text-xs">Proximo galon</p>
                  <p className="text-2xl">{streak.proximo_galon.icono}</p>
                  <p className="text-sm text-gray-300">{streak.proximo_galon.nombre}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Selector de color */}
        {selectedDate && (
          <Card className="card-mk border-gold bg-gray-900" data-testid="color-selector">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg">
                Evalua el dia {selectedDate}
              </CardTitle>
              <CardDescription className="text-gray-400">
                Como fue tu alimentacion?
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <Button onClick={() => handleColorSelect('verde')} disabled={submitting}
                  className="h-24 bg-green-600 hover:bg-green-500 flex flex-col items-center justify-center gap-2 text-white"
                  data-testid="btn-verde">
                  <span className="text-3xl">&#x1F7E2;</span>
                  <span className="text-sm font-medium">Verde</span>
                </Button>
                <Button onClick={() => handleColorSelect('amarillo')} disabled={submitting}
                  className="h-24 bg-yellow-500 hover:bg-yellow-400 flex flex-col items-center justify-center gap-2 text-black"
                  data-testid="btn-amarillo">
                  <span className="text-3xl">&#x1F7E1;</span>
                  <span className="text-sm font-medium">Amarillo</span>
                </Button>
                <Button onClick={() => handleColorSelect('rojo')} disabled={submitting}
                  className="h-24 bg-red-600 hover:bg-red-500 flex flex-col items-center justify-center gap-2 text-white"
                  data-testid="btn-rojo">
                  <span className="text-3xl">&#x1F534;</span>
                  <span className="text-sm font-medium">Rojo</span>
                </Button>
              </div>
              
              <div className="text-xs text-gray-400 space-y-1 bg-gray-800 p-3 rounded-lg">
                <p><span className="text-green-500">Verde:</span> Todas las comidas saludables. Agua como bebida principal.</p>
                <p><span className="text-yellow-500">Amarillo:</span> Un solo error (alcohol, refresco, comida poco saludable).</p>
                <p><span className="text-red-500">Rojo:</span> Dos o mas errores durante el dia.</p>
              </div>
              
              <Button variant="outline" onClick={() => setSelectedDate(null)}
                className="w-full border-gray-600 text-gray-300">
                Cancelar
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Calendario mensual */}
        <Card className="card-mk border-gray-800">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="icon" onClick={() => changeMonth(-1)} className="text-gray-400 hover:text-white">
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <CardTitle className="text-white flex items-center gap-2">
                <Calendar className="h-5 w-5 text-gold" />
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => changeMonth(1)} className="text-gray-400 hover:text-white">
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-gray-400 mb-4 text-center">
              Toca un dia para evaluarlo
              {isPro && <span className="text-purple-400 ml-1">| Franja purpura = dia de medicion PRO</span>}
            </p>
            <div className="grid grid-cols-7 gap-1">
              {renderCalendar()}
            </div>
          </CardContent>
        </Card>

        {/* Estadisticas del mes */}
        <Card className="card-mk border-gray-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-gold" />
              Estadisticas del mes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4 text-center">
              <div className="bg-green-900/30 rounded-lg p-3">
                <p className="text-2xl font-bold text-green-500">{stats.verde}</p>
                <p className="text-xs text-gray-400">Verdes</p>
              </div>
              <div className="bg-yellow-900/30 rounded-lg p-3">
                <p className="text-2xl font-bold text-yellow-500">{stats.amarillo}</p>
                <p className="text-xs text-gray-400">Amarillos</p>
              </div>
              <div className="bg-red-900/30 rounded-lg p-3">
                <p className="text-2xl font-bold text-red-500">{stats.rojo}</p>
                <p className="text-xs text-gray-400">Rojos</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-3">
                <p className="text-2xl font-bold text-gold">{stats.porcentaje_verde}%</p>
                <p className="text-xs text-gray-400">% Verde</p>
              </div>
            </div>
            
            {stats.total > 0 && (
              <div className="mt-4">
                <div className="h-3 rounded-full bg-gray-700 overflow-hidden flex">
                  <div className="bg-green-500 transition-all duration-500" style={{ width: `${(stats.verde / stats.total) * 100}%` }} />
                  <div className="bg-yellow-500 transition-all duration-500" style={{ width: `${(stats.amarillo / stats.total) * 100}%` }} />
                  <div className="bg-red-500 transition-all duration-500" style={{ width: `${(stats.rojo / stats.total) * 100}%` }} />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Galones MKdietal */}
        <Card className="card-mk border-gray-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <Award className="h-5 w-5 text-gold" />
              Tus galones MKdietal
            </CardTitle>
          </CardHeader>
          <CardContent>
            {badges.length === 0 ? (
              <p className="text-gray-400 text-center py-4">
                Aun no tienes galones. Consigue 3 dias verdes consecutivos!
              </p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {badges.map((badge) => (
                  <div 
                    key={badge.id}
                    className="bg-gray-800 rounded-lg p-3 text-center border border-gray-700"
                    style={{ borderColor: badge.galon_color }}
                  >
                    <span className="text-3xl">{badge.galon_icono}</span>
                    <p className="text-sm font-medium text-white mt-1">
                      {badge.galon_nombre.replace('MKdietal: ', '')}
                    </p>
                    <p className="text-xs text-gray-400">{badge.galon_descripcion}</p>
                  </div>
                ))}
              </div>
            )}
            
            <div className="mt-6">
              <p className="text-xs text-gray-500 mb-2">Galones disponibles:</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries({
                  3: { icono: '&#x1F331;', nombre: 'Buen inicio' },
                  5: { icono: '&#x1F4AA;', nombre: 'Constancia MK' },
                  7: { icono: '&#x2B50;', nombre: 'Semana limpia' },
                  14: { icono: '&#x1F525;', nombre: 'Habito en construccion' },
                  30: { icono: '&#x1F451;', nombre: 'Identidad MK' }
                }).map(([dias, galon]) => {
                  const hasGalon = badges.some(b => b.galon_nombre.includes(galon.nombre));
                  return (
                    <div 
                      key={dias}
                      className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${
                        hasGalon ? 'bg-green-900/50 text-green-400' : 'bg-gray-800 text-gray-500'
                      }`}
                      dangerouslySetInnerHTML={{ __html: `<span>${galon.icono}</span><span>${dias}d</span>` }}
                    />
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info */}
        <Card className="card-mk border-gray-800">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 text-center">
              MKdietal es un sistema de autoevaluacion basado en honestidad, no en castigo.
              El objetivo es visualizar patrones reales de alimentacion y crear conciencia.
            </p>
          </CardContent>
        </Card>

      </div>

      {/* Modal de Check-in - FUERA del flujo de cards */}
      <CheckinModal 
        isOpen={isCheckinOpen} 
        onClose={() => setIsCheckinOpen(false)} 
        onComplete={handleCheckinComplete}
      />
    </div>
  );
}

export default MKDietal;
